import { FormGroup } from '@angular/forms';
import { CANDIDATE_FILTER_CONSTANTS } from '@constants/candidate-filter.constants';

/**
 * Helper functions for candidate filter component
 */
export class CandidateFilterHelpers {
  
  /**
   * Extract value from form control, handling different value types
   */
  static extractFormValue(formValue: any): any {
    if (!formValue) return null;

    if (typeof formValue === 'object' && formValue.value) {
      return formValue.value;
    }

    if (Array.isArray(formValue)) {
      const selectedItems = formValue.filter((item) => item.selected === true);
      if (selectedItems.length > 1) {
        return selectedItems.map(item => item.value);
      } else if (selectedItems.length === 1) {
        return selectedItems[0].value;
      }
      return null;
    }

    return formValue;
  }

  /**
   * Extract saved value from different formats
   */
  static extractSavedValue(savedValue: any): string | string[] | null {
    if (!savedValue) return null;

    if (typeof savedValue === 'string') return savedValue;

    if (Array.isArray(savedValue)) {
      const selectedItems = savedValue.filter((item) => 
        typeof item === 'object' && item.selected === true
      );
      
      if (selectedItems.length > 0) {
        return selectedItems.length > 1 ? 
          selectedItems.map(item => item.value) : 
          selectedItems[0].value;
      } 
      if (savedValue.length > 0 && typeof savedValue[0] === 'string') {
        return savedValue.length > 1 ? savedValue : savedValue[0];
      }
    }

    if (typeof savedValue === 'object' && savedValue.value) {
      return savedValue.value;
    }

    return null;
  }

  /**
   * Convert date to ISO string safely
   */
  static convertDateToISOString(date: any): string | null {
    if (!date) return null;
    
    try {
      return date.toISOString?.() || date;
    } catch {
      return date;
    }
  }

  /**
   * Parse date from saved value
   */
  static parseSavedDate(dateValue: any): Date | null {
    if (!dateValue) return null;
    
    const parsedDate = new Date(dateValue);
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
  }

  /**
   * Initialize form with default structure
   */
  static createFormStructure() {
    return {
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_NAME]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_EXPERIENCE]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_LOCATION]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_STATUS]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_RECEIVED_ON]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_AVAILABLE_BY]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_DATE_APPLIED]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_HIRING_MANAGER]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_POSITION]: [null],
      [CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS.SELECTED_VENDOR]: [null]
    };
  }

  /**
   * Check if any form control has a meaningful value
   */
  static hasFormValues(formValue: any): boolean {
    return Object.values(formValue).some(value => 
      value !== null && value !== undefined && value !== ''
    );
  }

  /**
   * Find matching option in array by value
   */
  static findMatchingOption(options: any[], value: string): any | null {
    return options?.find((option) => option.value === value) || null;
  }
}
